/**
 * The DISCLOSURES text New York Life approved for the client report (WP-747).
 *
 * NYL sent this wording as "finalized and approved" and requires it word for word, so it lives
 * here once and every report surface renders the same paragraphs: the PDF report, the on-screen
 * report pages and the policy comparison PDF. A surface shows it instead of the standard
 * Waterlily box when the advisor belongs to the NYL pilot program (see `isNylPilotAdvisor`).
 *
 * The paragraphs are returned as `DisclosureParagraph` segments, the same shape the advisor's
 * custom disclosures use, so react-pdf, the DOM and jsPDF each render them with their own markup.
 * `nylReportDisclosure.test.ts` pins the full approved text: a wording change belongs to NYL
 * compliance and has to update that test on purpose.
 */
import type { DisclosureParagraph } from './parseCustomDisclosures';
import { possessive } from './possessive';

export const NYL_REPORT_DISCLOSURE_URL =
  'https://www.waterlily.com/planning-for-yourself/disclosures-disclaimers';
export const NYL_REPORT_DISCLOSURE_LINK_TEXT =
  'waterlily.com/planning-for-yourself/disclosures-disclaimers';

export function getNylReportDisclosureParagraphs(clientName: string): DisclosureParagraph[] {
  const clientPossessive = possessive(clientName);

  return [
    [
      {
        type: 'text',
        content: `${clientPossessive} projected long-term care cost and the age at which ${clientName} may need long-term care are predictions from models trained on other families' long-term care experiences. These predictions are estimates and are not guaranteed. ${clientPossessive} long-term care costs may be higher or lower, and care may begin sooner or later than what is shown in this report. Projected care needs and care costs may include services or expenses that would not qualify for benefits under a long-term care insurance policy. There is no guarantee that the amounts shown will be sufficient to cover ${clientPossessive} actual long-term care costs.`,
      },
    ],
    [
      {
        type: 'text',
        content:
          'The investment and insurance information provided in this report is for educational purposes only. This report is not investment or financial planning advice, a financial plan, tax advice, or a recommendation to buy, sell, or hold any security or follow any investment strategy or asset allocation. The report does not consider your full financial situation, needs or goals and should not be the sole basis for any investment or other financial decision. Investment assumptions and projected results shown are estimates for the modeled scenario only and are not guaranteed. Actual investment results may differ. Waterlily Caregiving, Inc. does not offer advisory or brokerage services, nor does it recommend or advise users to buy or sell particular stocks, securities, insurance policies, or other financial products. This report does not determine or guarantee your insurability. You will be required to complete an application and undergo underwriting to determine your eligibility for insurance coverage.',
      },
    ],
    [
      { type: 'text', content: 'Other disclosures on our models can be found on our site: ' },
      {
        type: 'link',
        content: NYL_REPORT_DISCLOSURE_LINK_TEXT,
        url: NYL_REPORT_DISCLOSURE_URL,
      },
      { type: 'text', content: '.' },
    ],
  ];
}
